import {
  Button,
  OutlinedSelect,
  OutlinedSelectOptionType,
  SearchBox,
  useMobile,
} from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';
import { useValidations } from '../../../hooks/useValidations';
import { useSectionsFormLoaderData } from './SectionsFormLoader';

const dictPrefix = 'ceClosingPeriod.searchControls';

const DEFAULT_VALUES = {
  year: null,
  schoolId: null,
  campusId: null,
  modalityId: null,
  searchSection: '',
  searchCourse: '',
};

interface FormValues {
  year: OutlinedSelectOptionType | null;
  schoolId: OutlinedSelectOptionType | null;
  campusId: OutlinedSelectOptionType | null;
  modalityId: OutlinedSelectOptionType | null;
  searchSection?: string;
  searchCourse?: string;
}

interface SearchParams {
  year: number;
  schoolId?: number;
  campusId?: number;
  modalityId?: number;
  searchSection?: string;
  searchCourse?: string;
}

interface SearchControlsProps {
  onSearch?: (params: SearchParams) => void;
  onClear?: () => void;
}

export function SearchControls({
  onSearch = () => null,
  onClear = () => null,
}: SearchControlsProps) {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { validateMinLength } = useValidations();

  const {
    years,
    currentYear,
    schools,
    campuses,
    modalities,
  } = useSectionsFormLoaderData();

  const yearsOptions = useMemo<OutlinedSelectOptionType[]>(() => {
    return years.map((p) => {
      return {
        value: p.id,
        label: p.name,
      };
    });
  }, [years]);

  const yearSelected = useMemo(() => {
    return yearsOptions.find(({ value }) => currentYear?.id === value);
  }, [yearsOptions, currentYear]);

  const { handleSubmit, control, reset } = useForm<FormValues>({
    defaultValues: { ...DEFAULT_VALUES, year: yearSelected },
  });

  const handleSearch = useCallback(
    (values: FormValues) => {
      const searchParams: SearchParams = {
        year: values.year?.value as number,
        schoolId: values.schoolId?.value as number,
        campusId: values.campusId?.value as number,
        modalityId: values.modalityId?.value as number,
        searchSection:
          values.searchSection?.trim() !== ''
            ? values.searchSection
            : undefined,
        searchCourse:
          values.searchCourse?.trim() !== '' ? values.searchCourse : undefined,
      };

      onSearch(searchParams);
    },
    [onSearch],
  );

  const handleClear = () => {
    reset({ ...DEFAULT_VALUES, year: yearSelected });
    onClear();
    onSearch({
      year: yearSelected?.value as number,
    });
  };

  const schoolsOptions = useMemo(
    () =>
      schools.map(({ id: value, name: label }) => ({
        label,
        value,
      })),
    [schools],
  );
  const campusesOptions = useMemo(
    () =>
      campuses.map(({ id: value, name: label }) => ({
        label,
        value,
      })),
    [campuses],
  );
  const modalitiesOptions = useMemo(
    () =>
      modalities.map(({ id: value, name: label }) => ({
        label,
        value,
      })),
    [modalities],
  );

  return (
    <Form onSubmit={handleSubmit(handleSearch)}>
      <Row className="pb-4 gutters-filters">
        <Col xs={12} md={6} lg={4}>
          <OutlinedSelect
            name="year"
            label={t(`${dictPrefix}.year`)}
            placeholder={t(`${dictPrefix}.yearPlaceholder`)}
            control={control}
            options={yearsOptions}
            isClearable={false}
          />
        </Col>

        <Col xs={12} md={6} lg={4}>
          <OutlinedSelect
            name="schoolId"
            label={t(`${dictPrefix}.schoolId`)}
            placeholder={t(`${dictPrefix}.schoolIdPlaceholder`)}
            control={control}
            options={schoolsOptions}
          />
        </Col>

        <Col xs={12} md={6} lg={4}>
          <OutlinedSelect
            name="campusId"
            label={t(`${dictPrefix}.campusId`)}
            placeholder={t(`${dictPrefix}.campusIdPlaceholder`)}
            control={control}
            options={campusesOptions}
          />
        </Col>

        <Col xs={12} md={6} lg={4}>
          <OutlinedSelect
            name="modalityId"
            label={t(`${dictPrefix}.modalityId`)}
            placeholder={t(`${dictPrefix}.modalityIdPlaceholder`)}
            control={control}
            options={modalitiesOptions}
          />
        </Col>

        <Col xs={12} md={6} lg={4}>
          <SearchBox
            name="searchSection"
            label={t(`${dictPrefix}.searchSection`)}
            placeholder={t(`${dictPrefix}.searchSectionPlaceholder`)}
            control={control}
            rules={{
              validate: {
                minLength: validateMinLength(3),
              },
            }}
          />
        </Col>

        <Col xs={12} md={6} lg={4}>
          <SearchBox
            name="searchCourse"
            label={t(`${dictPrefix}.searchCourse`)}
            placeholder={t(`${dictPrefix}.searchCoursePlaceholder`)}
            control={control}
            rules={{
              validate: {
                minLength: validateMinLength(3),
              },
            }}
          />
        </Col>

        <Col
          xs={12}
          md={{ size: 6 }}
          lg={{ size: 6 }}
          style={{ marginTop: isMobile ? 0 : '1.5rem' }}
        >
          <Row>
            <Col>
              <Button
                type="submit"
                text={t(`common.actions.search`)}
                size="md"
                fullwidth
              />
            </Col>
            <Col>
              <Button
                text={t(`common.actions.clean`)}
                size="md"
                fullwidth
                outlined
                onClick={handleClear}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
}
